/**
 * Baseado nos dados recebidos, informa se a conta informada segue o padrão definido do banco
 * @param banco Objeto com as informações do banco
 * @param tipoPagamento Número (string) que informa o tipo de pagamento
 * @param numeroConta O número da conta (string)
 * @param tipoContaPix Caso o tipo de pagamento seja "Pix (agência e conta)", parâmetro é o número (string) que informa o tipo conta pix
 * @returns Se o número da conta seguir o padrão definido pelo banco, retorna true. Caso contrário, false
 */
export const contaBancoValida = (banco: any, tipoPagamento: string, numeroConta: string, tipoContaPix: string): boolean => {

    const limitesContaBanco = getLimitesContaBanco(banco.numero);

    if (!limitesContaBanco) {
        return true;
    }

    switch (tipoPagamento) {
        case TipoPagamentoEnum.CONTA_CORRENTE:
            return (numeroConta.length >= limitesContaBanco.corrente.min) && (numeroConta.length <= limitesContaBanco.corrente.max);

        case TipoPagamentoEnum.CONTA_POUPANCA:
            return (numeroConta.length >= limitesContaBanco.poupanca.min) && (numeroConta.length <= limitesContaBanco.poupanca.max);

        case TipoPagamentoEnum.CONTA_SALARIO:
            return (numeroConta.length >= limitesContaBanco.salario.min) && (numeroConta.length <= limitesContaBanco.salario.max);

        case TipoPagamentoEnum.PIX_AGENCIA_CONTA:

            if (tipoContaPix === TipoContaPixEnum.CONTA_PAGAMENTO) {
                return (numeroConta.length >= limitesContaBanco.salario.min) && (numeroConta.length <= limitesContaBanco.salario.max);

            } else if (tipoContaPix === TipoContaPixEnum.CONTA_CORRENTE) {
                return (numeroConta.length >= limitesContaBanco.corrente.min) && (numeroConta.length <= limitesContaBanco.corrente.max);

            } else { // poupança
                return (numeroConta.length >= limitesContaBanco.poupanca.min) && (numeroConta.length <= limitesContaBanco.poupanca.max);

            }

        default:
            return true;
    }

};

/**
 * Baseado nos dados recebidos, informa se o dígito verificador segue o padrão definido do banco
 * @param banco Objeto com as informações do banco
 * @param tipoPagamento Número (string) que informa o tipo de pagamento
 * @param digitoVerificador O número do dígito verificador (string)
 * @param tipoContaPix Caso o tipo de pagamento seja "Pix (agência e conta)", parâmetro é o número (string) que informa o tipo conta pix
 * @returns Se o número do dígito verificador seguir o padrão definido pelo banco, retorna true. Caso contrário, false
 */
export const digitoVerificadorContaValido = (banco: any, tipoPagamento: string, digitoVerificador: string, tipoContaPix: string): boolean => {

    const limitesDigitoVerificador = getLimitesDigitoVerificador(banco.numero);

    switch (tipoPagamento) {
        case TipoPagamentoEnum.CONTA_CORRENTE:
            return (digitoVerificador.length >= limitesDigitoVerificador.corrente.min) && (digitoVerificador.length <= limitesDigitoVerificador.corrente.max);

        case TipoPagamentoEnum.CONTA_POUPANCA:
            return (digitoVerificador.length >= limitesDigitoVerificador.poupanca.min) && (digitoVerificador.length <= limitesDigitoVerificador.poupanca.max);

        case TipoPagamentoEnum.CONTA_SALARIO:
            return (digitoVerificador.length >= limitesDigitoVerificador.salario.min) && (digitoVerificador.length <= limitesDigitoVerificador.salario.max);

        case TipoPagamentoEnum.PIX_AGENCIA_CONTA:

            if (tipoContaPix === TipoContaPixEnum.CONTA_PAGAMENTO) {
                return (digitoVerificador.length >= limitesDigitoVerificador.salario.min) && (digitoVerificador.length <= limitesDigitoVerificador.salario.max);

            } else if (tipoContaPix === TipoContaPixEnum.CONTA_CORRENTE) {
                return (digitoVerificador.length >= limitesDigitoVerificador.corrente.min) && (digitoVerificador.length <= limitesDigitoVerificador.corrente.max);

            } else { // poupança
                return (digitoVerificador.length >= limitesDigitoVerificador.poupanca.min) && (digitoVerificador.length <= limitesDigitoVerificador.poupanca.max);

            }

        default:
            return true;
    }

};

/**
 * Baseado nos dados recebidos, retorna a mensagem a ser exibida para o usuário caso a conta não siga o padrão definido do banco
 * @param banco Objeto com as informações do banco
 * @param tipoPagamento Número (string) que informa o tipo de pagamento
 * @param numeroConta O número da conta (string)
 * @param tipoContaPix Caso o tipo de pagamento seja "Pix (agência e conta)", parâmetro é o número (string) que informa o tipo conta pix
 * @returns Mensagem de alerta para o usuário
 */
export const getMensagemContaBanco = (banco: any, tipoPagamento: string, numeroConta: string, tipoContaPix: string): string => {

    const limitesContaBanco = getLimitesContaBanco(banco.numero);

    switch (tipoPagamento) {
        case TipoPagamentoEnum.CONTA_CORRENTE:
            if (limitesContaBanco?.corrente.min === limitesContaBanco?.corrente.max) {
                return `Este banco normalmente usa ${limitesContaBanco?.corrente.min} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
            } else {
                return `Este banco normalmente usa entre ${limitesContaBanco?.corrente.min} e ${limitesContaBanco?.corrente.max} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
            }

        case TipoPagamentoEnum.CONTA_POUPANCA:
            if (limitesContaBanco?.poupanca.min === limitesContaBanco?.poupanca.max) {
                return `Este banco normalmente usa ${limitesContaBanco?.poupanca.min} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
            } else {
                return `Este banco normalmente usa entre ${limitesContaBanco?.poupanca.min} e ${limitesContaBanco?.poupanca.max} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
            }

        case TipoPagamentoEnum.CONTA_SALARIO:
            if (limitesContaBanco?.salario.min === limitesContaBanco?.salario.max) {
                return `Este banco normalmente usa ${limitesContaBanco?.salario.min} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
            } else {
                return `Este banco normalmente usa entre ${limitesContaBanco?.salario.min} e ${limitesContaBanco?.salario.max} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
            }

        case TipoPagamentoEnum.PIX_AGENCIA_CONTA:

            if (tipoContaPix === TipoContaPixEnum.CONTA_PAGAMENTO) {
                if (limitesContaBanco?.salario.min === limitesContaBanco?.salario.max) {
                    return `Este banco normalmente usa ${limitesContaBanco?.salario.min} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
                } else {
                    return `Este banco normalmente usa entre ${limitesContaBanco?.salario.min} e ${limitesContaBanco?.salario.max} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
                }

            } else if (tipoContaPix === TipoContaPixEnum.CONTA_CORRENTE) {
                if (limitesContaBanco?.corrente.min === limitesContaBanco?.corrente.max) {
                    return `Este banco normalmente usa ${limitesContaBanco?.corrente.min} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
                } else {
                    return `Este banco normalmente usa entre ${limitesContaBanco?.corrente.min} e ${limitesContaBanco?.corrente.max} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
                }

            } else { // poupança
                if (limitesContaBanco?.poupanca.min === limitesContaBanco?.poupanca.max) {
                    return `Este banco normalmente usa ${limitesContaBanco?.poupanca.min} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
                } else {
                    return `Este banco normalmente usa entre ${limitesContaBanco?.poupanca.min} e ${limitesContaBanco?.poupanca.max} dígitos na conta. Você informou ${numeroConta.length}. Verifique os dados informados.`;
                }

            }

    }

    return '';
};

/**
 * Baseado no banco informado através de seu número (string), retorna a quantidade limite de dígitos da conta para os tipos de conta possíveis
 * @param numeroBanco Número (string) que serve como identificador do banco
 * @returns Objeto com os limites máximos e mínimos de dígitos da conta do banco para os tipos de conta possíveis
 */
export const getLimitesContaBanco = (numeroBanco: string) => {

    switch (numeroBanco) {
        // itau
        case '341':
            return {
                corrente: {min: 5, max: 5},
                poupanca: {min: 5, max: 5},
                salario: {min: 5, max: 6}
            };

        // bradesco
        case '237':
            return {
                corrente: {min: 5, max: 7},
                poupanca: {min: 5, max: 7},
                salario: {min: 5, max: 7}
            };

        // banco do brasil
        case '001':
            return {
                corrente: {min: 5, max: 9},
                poupanca: {min: 5, max: 9},
                salario: {min: 5, max: 9}
            };

        // santander
        case '033':
            return {
                corrente: {min: 8, max: 12},
                poupanca: {min: 7, max: 9},
                salario: {min: 8, max: 12}
            };

        // nubank
        case '260':
            return {
                corrente: {min: 6, max: 11},
                poupanca: {min: 6, max: 11},
                salario: {min: 6, max: 11}
            };

        // inter
        case '077':
            return {
                corrente: {min: 7, max: 8},
                poupanca: {min: 7, max: 8},
                salario: {min: 7, max: 8},
            };

        // banco c6
        case '336':
            return {
                corrente: {min: 6, max: 9},
                poupanca: {min: 7, max: 8},
                salario: {min: 5, max: 7}
            };

        // caixa economica
        case '104':
            return {
                corrente: {min: 5, max: 12},
                poupanca: {min: 5, max: 9},
                salario: {min: 8, max: 12}
            };

        // safra
        case '422':
            return {
                corrente: {min: 6, max: 7},
                poupanca: {min: 6, max: 7},
                salario: {min: 6, max: 7},
            };

        // neon
        case '536':
            return {
                corrente: {min: 6, max: 8},
                poupanca: {min: 6, max: 8},
                salario: {min: 6, max: 8},
            };

        // banrisul
        case '041':
            return {
                corrente: {min: 9, max: 9},
                poupanca: {min: 9, max: 9},
                salario: {min: 9, max: 9},
            };

        // banco não mapeado
        default:
            return null;
    }

};

/**
 * Baseado no banco informado através de seu número (string), retorna a quantidade limite do dígito verificador para os tipos de conta possíveis
 * @param numeroBanco Número (string) que serve como identificador do banco
 * @returns Objeto com os limites máximos e mínimos do dígito verificador do banco para os tipos de conta possíveis
 */
export const getLimitesDigitoVerificador = (numeroBanco: string) => {

    switch (numeroBanco) {
        // bradesco
        case '237':
            return {
                corrente: {min: 1, max: 1},
                poupanca: {min: 1, max: 1},
                salario: {min: 1, max: 2}
            };

        default:
            return {
                corrente: {min: 1, max: 1},
                poupanca: {min: 1, max: 1},
                salario: {min: 1, max: 1}
            };
    }

};


/**
 * Baseado nos dados recebidos, retorna a mensagem a ser exibida para o usuário caso o dígito verificador não siga o padrão definido do banco
 * @param banco Objeto com as informações do banco
 * @param tipoPagamento Número (string) que informa o tipo de pagamento
 * @param digitoVerificador O número do dígito verificador (string)
 * @param tipoContaPix Caso o tipo de pagamento seja "Pix (agência e conta)", parâmetro é o número (string) que informa o tipo conta pix
 * @returns Mensagem de alerta para o usuário
 */
export const getMensagemDigitoVerificador = (banco: any, tipoPagamento: string, digitoVerificador: string, tipoContaPix: string): string => {

    const limitesDigitoVerificador = getLimitesDigitoVerificador(banco.numero);

    switch (tipoPagamento) {
        case TipoPagamentoEnum.CONTA_CORRENTE:
            if (limitesDigitoVerificador?.corrente.min === limitesDigitoVerificador?.corrente.max) {
                return `Este banco normalmente usa ${limitesDigitoVerificador?.corrente.min} dígito(s) no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
            } else {
                return `Este banco normalmente usa entre ${limitesDigitoVerificador?.corrente.min} e ${limitesDigitoVerificador?.corrente.max} dígitos no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
            }

        case TipoPagamentoEnum.CONTA_POUPANCA:
            if (limitesDigitoVerificador?.poupanca.min === limitesDigitoVerificador?.poupanca.max) {
                return `Este banco normalmente usa ${limitesDigitoVerificador?.poupanca.min} dígito(s) no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
            } else {
                return `Este banco normalmente usa entre ${limitesDigitoVerificador?.poupanca.min} e ${limitesDigitoVerificador?.poupanca.max} dígitos no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
            }

        case TipoPagamentoEnum.CONTA_SALARIO:
            if (limitesDigitoVerificador?.salario.min === limitesDigitoVerificador?.salario.max) {
                return `Este banco normalmente usa ${limitesDigitoVerificador?.salario.min} dígito(s) no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
            } else {
                return `Este banco normalmente usa entre ${limitesDigitoVerificador?.salario.min} e ${limitesDigitoVerificador?.salario.max} dígitos no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
            }

        case TipoPagamentoEnum.PIX_AGENCIA_CONTA:

            if (tipoContaPix === TipoContaPixEnum.CONTA_PAGAMENTO) {
                if (limitesDigitoVerificador?.salario.min === limitesDigitoVerificador?.salario.max) {
                    return `Este banco normalmente usa ${limitesDigitoVerificador?.salario.min} dígito(s) no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
                } else {
                    return `Este banco normalmente usa entre ${limitesDigitoVerificador?.salario.min} e ${limitesDigitoVerificador?.salario.max} dígitos no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
                }

            } else if (tipoContaPix === TipoContaPixEnum.CONTA_CORRENTE) {
                if (limitesDigitoVerificador?.corrente.min === limitesDigitoVerificador?.corrente.max) {
                    return `Este banco normalmente usa ${limitesDigitoVerificador?.corrente.min} dígito(s) no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
                } else {
                    return `Este banco normalmente usa entre ${limitesDigitoVerificador?.corrente.min} e ${limitesDigitoVerificador?.corrente.max} dígitos no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
                }

            } else { // poupança
                if (limitesDigitoVerificador?.poupanca.min === limitesDigitoVerificador?.poupanca.max) {
                    return `Este banco normalmente usa ${limitesDigitoVerificador?.poupanca.min} dígito(s) no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
                } else {
                    return `Este banco normalmente usa entre ${limitesDigitoVerificador?.poupanca.min} e ${limitesDigitoVerificador?.poupanca.max} dígitos no DV. Você informou ${digitoVerificador.length}. Verifique os dados informados.`;
                }

            }

    }

    return '';
};

export enum TipoPagamentoEnum {
    CONTA_CORRENTE = '1',
    CONTA_POUPANCA = '2',
    CONTA_SALARIO = '4',
    PIX_AGENCIA_CONTA = '64'
}

export enum TipoContaPixEnum {
    CONTA_PAGAMENTO = '1', // salário
    CONTA_CORRENTE = '2',
    CONTA_POUPANCA = '3'
}
